import React, { createContext, useContext } from 'react';
import { ROLES, type Role } from './role.ts';
import type { ModuleConfig, SubscreenConfig } from './modules';

const RoleContext = createContext<Role | null>(null);

interface RoleProviderProps {
  role?: Role;
  children: React.ReactNode;
}

export function RoleProvider({ role: roleProp, children }: RoleProviderProps) {
  let role: Role;

  if (roleProp !== undefined) {
    role = roleProp;
  } else {
    const envRole = import.meta.env.DEV
      ? (import.meta.env.VITE_ADMIN_DEV_ROLE as string | undefined)
      : undefined;
    if (!envRole) {
      role = 'chats';
    } else if ((ROLES as readonly string[]).includes(envRole)) {
      role = envRole as Role;
    } else {
      console.warn(
        `[RoleProvider] VITE_ADMIN_DEV_ROLE="${envRole}" is not a valid role. Valid roles: ${ROLES.join(', ')}. Falling back to "chats".`,
      );
      role = 'chats';
    }
  }

  return <RoleContext.Provider value={role}>{children}</RoleContext.Provider>;
}

export function useRole(): Role {
  const role = useContext(RoleContext);
  if (role === null) {
    throw new Error(
      '[useRole] must be used inside <RoleProvider>. Wrap your component tree with <RoleProvider>.',
    );
  }
  return role;
}

export function useOptionalRole(defaultRole: Role = 'chats'): Role {
  return useContext(RoleContext) ?? defaultRole;
}

export function canSeeModule(role: Role, mod: ModuleConfig): boolean {
  return mod.access[role] !== 'none';
}

export function canSeeSubscreen(role: Role, sub: SubscreenConfig): boolean {
  return sub.access[role] !== 'none';
}

export function canEdit(role: Role, mod: ModuleConfig): boolean {
  return mod.access[role] === 'full';
}

export function canExitToLauncher(role: Role): boolean {
  return role === 'management';
}
